import { cn } from '../lib/cn';

export interface TabItem<T extends string> {
  value: T;
  label: string;
  badge?: number;
}

interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

export function Tabs<T extends string>({ items, value, onChange, className }: TabsProps<T>) {
  return (
    <div role="tablist" className={cn('flex items-center gap-6 border-b border-ops-border', className)}>
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(
              'relative -mb-px flex items-center gap-2 pb-3 pt-1 text-ops-body font-medium',
              'transition-colors duration-ops ease-ops',
              active ? 'text-ops-primary' : 'text-ops-text-secondary hover:text-ops-text',
            )}
          >
            {item.label}
            {typeof item.badge === 'number' && (
              <span
                className={cn(
                  'rounded-ops-pill px-1.5 py-0.5 text-ops-micro font-semibold',
                  active ? 'bg-ops-primary-soft text-ops-primary' : 'bg-ops-surface-2 text-ops-text-tertiary',
                )}
              >
                {item.badge}
              </span>
            )}
            {active && <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-ops-pill bg-ops-primary" />}
          </button>
        );
      })}
    </div>
  );
}
